'use client'

import {
    StoreConfig,
    checkStoreLimit,
    checkSubdomainAvailability,
    createStoreConfig,
    getStoreConfig,
    updateStoreConfig,
    uploadStoreImage,
} from '@/lib/api/commerce/store-config'
import { useAuth } from '@/store/hooks'
import { useImageCrop } from '@/components/image-crop'
import { useCallback, useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdCheck,
    MdClose,
    MdCode,
    MdPalette,
    MdSave,
    MdSearch,
    MdShare,
    MdStorefront
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

// Parcelamento NÃO se configura aqui. A loja respeita a política financeira da empresa
// (Financeiro > Configurações > Checkout), a mesma que monta as parcelas na página de
// cobrança — ter as duas fazia a vitrine anunciar um parcelamento que o pagamento não
// oferecia.
type Tab = 'basic' | 'appearance' | 'seo-social'

export default function StoreSettingsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    const [loading, setLoading] = useState(!!id)
    const [saving, setSaving] = useState(false)
    const [configId, setConfigId] = useState<number | null>(null)
    const [activeTab, setActiveTab] = useState<Tab>('basic')
    const [storeLimitInfo, setStoreLimitInfo] = useState<{
        exceedsLimit: boolean; currentPrice: number; newPrice: number; currentStores: number; maxStores: number
    } | null>(null)

    // Campos do formulário
    const [name, setName] = useState('')
    const [subdomain, setSubdomain] = useState('')
    const [customDomain, setCustomDomain] = useState('')
    const [description, setDescription] = useState('')
    const [logo, setLogo] = useState('')
    const [favicon, setFavicon] = useState('')
    const [active, setActive] = useState(false)
    const [uploadingLogo, setUploadingLogo] = useState(false)
    const [uploadingFavicon, setUploadingFavicon] = useState(false)

    // SEO
    const [seoTitle, setSeoTitle] = useState('')
    const [seoDescription, setSeoDescription] = useState('')
    const [seoKeywords, setSeoKeywords] = useState('')

    // Social
    const [instagram, setInstagram] = useState('')
    const [facebook, setFacebook] = useState('')
    const [twitter, setTwitter] = useState('')
    const [whatsapp, setWhatsapp] = useState('')

    // CSS customizado
    const [customCss, setCustomCss] = useState('')

    // Verificação de subdomínio
    const [subdomainStatus, setSubdomainStatus] = useState<
        'idle' | 'checking' | 'available' | 'unavailable'
    >('idle')
    const [subdomainTimer, setSubdomainTimer] = useState<NodeJS.Timeout | null>(
        null,
    )

    useEffect(() => {
        if (isEditing) loadData()
        else loadStoreLimit()
    }, [id])

    const loadStoreLimit = async () => {
        const companyId = user?.company?.id
        if (!companyId) return
        try {
            const res = await checkStoreLimit(companyId)
            if (res.success && res.result && !res.result.unlimited && res.result.exceedsLimit) {
                setStoreLimitInfo({
                    exceedsLimit: true,
                    currentPrice: res.result.currentPrice || 0,
                    newPrice: res.result.newPrice,
                    currentStores: res.result.currentStores,
                    maxStores: res.result.maxStores,
                })
            }
        } catch { /* silently */ }
    }

    const loadData = async () => {
        if (!id) return

        setLoading(true)
        try {
            const res = await getStoreConfig(parseInt(id, 10))
            if (res.success && res.result) {
                const config = res.result
                setConfigId(config.id)
                setName(config.name || '')
                setSubdomain(config.subdomain || '')
                setCustomDomain(config.customDomain || '')
                setDescription(config.description || '')
                setLogo(config.logo || '')
                setFavicon(config.favicon || '')
                setActive(config.active)

                setSeoTitle(config.seoTitle || '')
                setSeoDescription(config.seoDescription || '')
                setSeoKeywords(config.seoKeywords || '')

                if (config.socialLinks) {
                    setInstagram(config.socialLinks.instagram || '')
                    setFacebook(config.socialLinks.facebook || '')
                    setTwitter(config.socialLinks.twitter || '')
                    setWhatsapp(config.socialLinks.whatsapp || '')
                }

                setCustomCss(config.customCss || '')

                setSubdomainStatus('idle')
            }
        } catch {
            // Sem configuração ainda, formulário vazio
        } finally {
            setLoading(false)
        }
    }

    // Logo tem formato livre (horizontal, vertical, com transparência); o favicon
    // é sempre quadrado e pequeno.
    const logoCrop = useImageCrop({ title: 'Ajustar logo', output: { maxWidth: 512 } })
    const faviconCrop = useImageCrop({ title: 'Ajustar favicon', aspect: 1, output: { maxWidth: 256 } })

    const handleImageUpload = async (
        field: 'logo' | 'favicon',
        rawFile: File,
    ) => {
        if (!configId) {
            toast.error('Salve as configurações básicas antes de enviar imagens')
            return
        }

        const cropFile = field === 'logo' ? logoCrop.cropFile : faviconCrop.cropFile
        const file = await cropFile(rawFile)
        if (!file) return

        const setUploading = field === 'logo' ? setUploadingLogo : setUploadingFavicon
        const setUrl = field === 'logo' ? setLogo : setFavicon

        setUploading(true)
        try {
            const res = await uploadStoreImage(configId, field, file)
            if (res.success && res.result) {
                setUrl(res.result[field] || '')
                toast.success(
                    field === 'logo'
                        ? 'Logo enviado com sucesso'
                        : 'Favicon enviado com sucesso',
                )
            } else {
                toast.error('Erro ao enviar imagem')
            }
        } catch {
            toast.error('Erro ao enviar imagem')
        } finally {
            setUploading(false)
        }
    }

    const handleSubdomainChange = useCallback(
        (value: string) => {
            const normalized = value
                .toLowerCase()
                .replace(/[^a-z0-9-]/g, '')
            setSubdomain(normalized)
            setSubdomainStatus('idle')

            if (subdomainTimer) {
                clearTimeout(subdomainTimer)
            }

            if (normalized.length >= 3) {
                const timer = setTimeout(async () => {
                    setSubdomainStatus('checking')
                    try {
                        const res =
                            await checkSubdomainAvailability(normalized)
                        if (res.success && res.result) {
                            setSubdomainStatus(
                                res.result.available
                                    ? 'available'
                                    : 'unavailable',
                            )
                        }
                    } catch {
                        setSubdomainStatus('idle')
                    }
                }, 500)
                setSubdomainTimer(timer)
            }
        },
        [subdomainTimer],
    )

    const handleSave = async () => {
        const companyId = user?.company?.id
        if (!companyId) {
            toast.error('Empresa não encontrada')
            return
        }

        if (!name.trim()) {
            toast.error('Nome da loja é obrigatório')
            return
        }

        if (!subdomain.trim() || subdomain.length < 3) {
            toast.error('Subdomínio deve ter pelo menos 3 caracteres')
            return
        }

        setSaving(true)
        try {
            const data: Partial<StoreConfig> = {
                companyId,
                name: name.trim(),
                subdomain: subdomain.trim(),
                customDomain: customDomain.trim() || undefined,
                description: description.trim() || undefined,
                logo: logo.trim() || undefined,
                favicon: favicon.trim() || undefined,
                seoTitle: seoTitle.trim() || undefined,
                seoDescription: seoDescription.trim() || undefined,
                seoKeywords: seoKeywords.trim() || undefined,
                socialLinks: {
                    instagram: instagram.trim() || undefined,
                    facebook: facebook.trim() || undefined,
                    twitter: twitter.trim() || undefined,
                    whatsapp: whatsapp.trim() || undefined,
                },
                customCss: customCss.trim() || undefined,
                active,
            }

            let res
            if (configId) {
                res = await updateStoreConfig(configId, data)
            } else {
                res = await createStoreConfig(data)
            }

            if (res.success) {
                if (res.result) {
                    setConfigId(res.result.id)
                }
                toast.success(
                    configId
                        ? 'Configurações atualizadas com sucesso'
                        : 'Loja criada com sucesso',
                )
                if (!configId && res.result) {
                    navigate(`/commerce/stores/edit/${res.result.id}`, { replace: true })
                }
            } else {
                toast.error(
                    (res as any).message || 'Erro ao salvar configurações',
                )
            }
        } catch {
            toast.error('Erro ao salvar configurações')
        } finally {
            setSaving(false)
        }
    }

    const handleToggleActive = async (value: boolean) => {
        setActive(value)
        if (!configId) return
        try {
            await updateStoreConfig(configId, { active: value })
            toast.success(value ? 'Loja ativada' : 'Loja desativada')
        } catch {
            setActive(!value)
            toast.error('Erro ao alterar status da loja')
        }
    }

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
        { key: 'basic', label: 'Informações Básicas', icon: <MdStorefront /> },
        { key: 'appearance', label: 'Aparência', icon: <MdPalette /> },
        { key: 'seo-social', label: 'SEO e Redes Sociais', icon: <MdSearch /> },
    ]

    return (
        <div className="mx-auto max-w-4xl space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('/commerce/stores')}
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <MdStorefront className="text-secondary text-3xl" />
                    <div>
                        <h1 className="text-2xl font-bold">
                            {isEditing ? 'Editar Loja' : 'Nova Loja'}
                        </h1>
                        <p className="text-base-content/60 text-sm">
                            Configure sua loja online
                        </p>
                    </div>
                </div>
                {configId && (
                    <label className="label cursor-pointer gap-2">
                        <span className="label-text font-medium">
                            {active ? 'Ativa' : 'Inativa'}
                        </span>
                        <input
                            type="checkbox"
                            className="toggle toggle-secondary"
                            checked={active}
                            onChange={(e) => handleToggleActive(e.target.checked)}
                        />
                    </label>
                )}
            </div>

            {/* Aviso de loja extra */}
            {!isEditing && storeLimitInfo?.exceedsLimit && (
                <div className="alert alert-warning">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 shrink-0 stroke-current" fill="none" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                        <h3 className="font-bold">Loja extra</h3>
                        <p className="text-sm">
                            Você já possui {storeLimitInfo.currentStores} de {storeLimitInfo.maxStores} loja(s) inclusa(s) no plano.
                            Ao criar esta loja, o valor da sua assinatura passará de{' '}
                            <strong>R$ {(storeLimitInfo.currentPrice / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> para{' '}
                            <strong>R$ {(storeLimitInfo.newPrice / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> por mês.
                        </p>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div role="tablist" className="tabs tabs-bordered">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        role="tab"
                        className={`tab gap-2 ${activeTab === tab.key ? 'tab-active font-semibold' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab: Informações Básicas */}
            {activeTab === 'basic' && (
                <div className="card bg-base-100 shadow-sm">
                    <div className="card-body space-y-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Nome da Loja *
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Minha Loja Online"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Subdomínio *
                                    </span>
                                </label>
                                <div className="flex items-center gap-2">
                                    <div className="relative flex-1">
                                        <input
                                            type="text"
                                            className={`input input-bordered w-full pr-10 ${subdomainStatus === 'available'
                                                ? 'input-success'
                                                : subdomainStatus ===
                                                    'unavailable'
                                                    ? 'input-error'
                                                    : ''
                                                }`}
                                            placeholder="minha-loja"
                                            value={subdomain}
                                            onChange={(e) =>
                                                handleSubdomainChange(
                                                    e.target.value,
                                                )
                                            }
                                        />
                                        <div className="absolute top-1/2 right-3 -translate-y-1/2">
                                            {subdomainStatus === 'checking' && (
                                                <span className="loading loading-spinner loading-xs" />
                                            )}
                                            {subdomainStatus === 'available' && (
                                                <MdCheck className="text-lg text-success" />
                                            )}
                                            {subdomainStatus === 'unavailable' && (
                                                <MdClose className="text-lg text-error" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <label className="label">
                                    <span className="label-text-alt text-base-content/50">
                                        {subdomain
                                            ? `${subdomain}.cliquei.shop`
                                            : 'seu-nome.cliquei.shop'}
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Domínio Personalizado
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="cliquei.shop.br"
                                value={customDomain}
                                onChange={(e) => setCustomDomain(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ''))}
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    Opcional. Configure um CNAME no DNS do seu domínio apontando para{' '}
                                    <strong>{subdomain || 'seu-subdominio'}.cliquei.shop</strong>
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Descrição
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Descreva sua loja..."
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Logo
                                    </span>
                                </label>
                                <input
                                    type="file"
                                    className="file-input file-input-bordered w-full"
                                    accept="image/*"
                                    disabled={uploadingLogo || !configId}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0]
                                        if (file) handleImageUpload('logo', file)
                                    }}
                                />
                                {!configId && (
                                    <label className="label">
                                        <span className="label-text-alt text-warning">
                                            Salve as configurações primeiro para enviar o logo
                                        </span>
                                    </label>
                                )}
                                {uploadingLogo && (
                                    <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                                        <span className="loading loading-spinner loading-xs" />
                                        Enviando...
                                    </div>
                                )}
                                {logo && !uploadingLogo && (
                                    <div className="mt-2">
                                        <img
                                            src={logo}
                                            alt="Logo"
                                            className="h-16 w-auto rounded border"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Favicon
                                    </span>
                                </label>
                                <input
                                    type="file"
                                    className="file-input file-input-bordered w-full"
                                    accept="image/*"
                                    disabled={uploadingFavicon || !configId}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0]
                                        if (file) handleImageUpload('favicon', file)
                                    }}
                                />
                                {!configId && (
                                    <label className="label">
                                        <span className="label-text-alt text-warning">
                                            Salve as configurações primeiro para enviar o favicon
                                        </span>
                                    </label>
                                )}
                                {uploadingFavicon && (
                                    <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                                        <span className="loading loading-spinner loading-xs" />
                                        Enviando...
                                    </div>
                                )}
                                {favicon && !uploadingFavicon && (
                                    <div className="mt-2">
                                        <img
                                            src={favicon}
                                            alt="Favicon"
                                            className="h-8 w-auto rounded border"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tab: Aparência */}
            {activeTab === 'appearance' && (
                <div className="space-y-6">
                    {/* Tema: cores, fonte, disposição e seções ficam no editor de tema */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body gap-4">
                            <h2 className="card-title text-lg">
                                <MdPalette className="text-secondary" />
                                Tema da loja
                            </h2>
                            <p className="text-sm text-base-content/70">
                                Cores, fonte, disposição, banner e as seções da página inicial são
                                configurados no editor de tema, com a prévia da loja ao lado. As
                                alterações ficam em rascunho até você publicar.
                            </p>
                            {configId ? (
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={() => navigate(`/commerce/themes/${configId}`)}
                                    >
                                        <MdPalette /> Abrir o editor de tema
                                    </button>
                                </div>
                            ) : (
                                <p className="text-sm text-base-content/60">
                                    Salve as informações básicas para criar a loja e liberar o editor de tema.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* CSS Customizado */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <h2 className="card-title text-lg">
                                <MdCode className="text-secondary" />
                                CSS Customizado
                            </h2>
                            <p className="text-base-content/60 text-sm">
                                Adicione CSS personalizado para ajustes finos na
                                aparência da sua loja.
                            </p>

                            <div className="form-control w-full">
                                <textarea
                                    className="textarea textarea-bordered w-full font-mono text-sm"
                                    placeholder={`.hero-banner {\n  border-radius: 12px;\n}`}
                                    rows={8}
                                    value={customCss}
                                    onChange={(e) => setCustomCss(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tab: SEO e Redes Sociais */}
            {activeTab === 'seo-social' && (
                <div className="space-y-6">
                    {/* SEO */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <h2 className="card-title text-lg">
                                <MdSearch className="text-secondary" />
                                Otimização para Buscadores (SEO)
                            </h2>

                            <div className="space-y-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Título SEO
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Título que aparecerá no Google"
                                        value={seoTitle}
                                        onChange={(e) => setSeoTitle(e.target.value)}
                                        maxLength={60}
                                    />
                                    <label className="label">
                                        <span className="label-text-alt">
                                            {seoTitle.length}/60 caracteres
                                        </span>
                                    </label>
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Descrição SEO
                                        </span>
                                    </label>
                                    <textarea
                                        className="textarea textarea-bordered w-full"
                                        placeholder="Descrição que aparecerá no Google"
                                        rows={2}
                                        value={seoDescription}
                                        onChange={(e) =>
                                            setSeoDescription(e.target.value)
                                        }
                                        maxLength={160}
                                    />
                                    <label className="label">
                                        <span className="label-text-alt">
                                            {seoDescription.length}/160 caracteres
                                        </span>
                                    </label>
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Palavras-chave
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="roupas, moda, acessórios (separadas por vírgula)"
                                        value={seoKeywords}
                                        onChange={(e) => setSeoKeywords(e.target.value)}
                                    />
                                </div>

                                {/* Preview do Google */}
                                {(seoTitle || name) && (
                                    <div className="rounded-lg bg-white p-4">
                                        <p className="mb-1 text-sm text-green-700">
                                            {subdomain
                                                ? `${subdomain}.cliquei.shop`
                                                : 'sua-loja.cliquei.shop'}
                                        </p>
                                        <p className="mb-1 text-xl text-blue-700 hover:underline">
                                            {seoTitle || name}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            {seoDescription ||
                                                description ||
                                                'Descrição da sua loja online...'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Redes Sociais */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <h2 className="card-title text-lg">
                                <MdShare className="text-secondary" />
                                Redes Sociais
                            </h2>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Instagram
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="https://instagram.com/sua-loja"
                                        value={instagram}
                                        onChange={(e) => setInstagram(e.target.value)}
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Facebook
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="https://facebook.com/sua-loja"
                                        value={facebook}
                                        onChange={(e) => setFacebook(e.target.value)}
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Twitter / X
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="https://twitter.com/sua-loja"
                                        value={twitter}
                                        onChange={(e) => setTwitter(e.target.value)}
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            WhatsApp
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="5511999999999"
                                        value={whatsapp}
                                        onChange={(e) => setWhatsapp(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Botão Salvar fixo no final */}
            <div className="flex justify-end pb-6">
                <button
                    className="btn btn-secondary btn-lg"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? (
                        <span className="loading loading-spinner loading-sm" />
                    ) : (
                        <MdSave className="text-lg" />
                    )}
                    Salvar Configurações
                </button>
            </div>
            {logoCrop.cropDialog}
            {faviconCrop.cropDialog}
        </div>
    )
}
